import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  Clock,
  Gift,
  Printer,
  Calendar,
  Tag,
  Home,
  Sparkles,
  Bell,
  RefreshCw,
} from 'lucide-react';
import api from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

const ICON_MAP = {
  'user-plus': UserPlus,
  clock: Clock,
  gift: Gift,
  printer: Printer,
  calendar: Calendar,
  tag: Tag,
  home: Home,
  sparkles: Sparkles,
};

const PRIORITY_CLASSES = {
  high: 'border-red-500/50 bg-red-500/10 text-red-300 hover:bg-red-500/20',
  medium: 'border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20',
  low: 'border-blue-500/50 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20',
};

/**
 * Approval Hub: aggregated bar shown at the top of the admin dashboard.
 *
 * Lists every pending approval queue (loyalty members/awards/extensions,
 * leaves, price changes, offers, housing residents, etc.) as a clickable chip.
 *
 * Hidden entirely when there is nothing pending.
 */
const PendingApprovalsBar = () => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, chips: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/approval-hub');
      setData(res.data || { total: 0, chips: [] });
    } catch {
      setData({ total: 0, chips: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 60s
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, [fetchData]);

  if (loading) return null;
  if (!data.total) return null;

  return (
    <div
      className="rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-3 mb-4 animate-in fade-in slide-in-from-top-2"
      data-testid="approval-hub-bar"
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-400 animate-pulse" />
          <span className="text-sm font-bold">
            {isArabic
              ? `لديك ${data.total} طلب بانتظار اعتمادك`
              : `${data.total} approval${data.total > 1 ? 's' : ''} pending your action`}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={fetchData}
          data-testid="approval-hub-refresh"
          title={isArabic ? 'تحديث' : 'Refresh'}
        >
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {data.chips.map((c) => {
          const Icon = ICON_MAP[c.icon] || Bell;
          const cls = PRIORITY_CLASSES[c.priority] || PRIORITY_CLASSES.medium;
          return (
            <button
              key={c.key}
              onClick={() => navigate(c.link)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition ${cls}`}
              data-testid={`approval-chip-${c.key}`}
              title={isArabic ? c.label_ar : c.label_en}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{isArabic ? c.label_ar : c.label_en}</span>
              <span className="bg-white/20 rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 text-[11px] font-extrabold">
                {c.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PendingApprovalsBar;
